import Button from 'tailwind-components/Button/Button';
import React, { useState } from 'react';
import propTypes from 'prop-types';
import style from './PopUpCustomModal.module.scss';
import Modal from 'tailwind-components/Modal/Modal';
import { useNavigate } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { safePostCall } from 'helpers/firebaseSetup';
import endpoints from 'constants/endpoints';
import { useSelector } from 'react-redux';
import { sanitizeDirtyHTML } from 'helpers/strings';
import useViewAs from 'hooks/useViewAs';

export default function PopUpCustomModal({ popUp, closePopUp }) {
  const navigate = useNavigate();
  const zohoData = useSelector(state => state.user.zohoData);
  const { isViewingAs } = useViewAs();

  const zohoId = zohoData.id;

  const [dontShowAgain, setDontShowAgain] = useState(false);

  // this will only be true for pop-ups that are part of the push notifications and pop ups list or if the user is viewing as an agent
  // we don't want to mark as read if the user is viewing as an agent so the real user will see the pop-up again
  const isReadOnly = popUp.type === 'pop-up' || isViewingAs;

  const handleClose = () => {
    // we'll only mark as read if user has checked the checkbox or
    // if the checkbox is not present, we'll mark as read -> assume it's a one-time pop-up
    if ((dontShowAgain || !popUp.dont_show_again_checkbox) && !isReadOnly) {
      // we don't need to wait for the response or check the response -> no need to await
      safePostCall(endpoints.awsProxy.genericPost, {
        agentId: zohoId,
        popUpId: popUp.id,
        endpoint: endpoints.zoho.markPopUpAsRead,
        isZohoRequest: true
      });
    }
    closePopUp();
  };

  return (
    <Modal
      active
      size={popUp.size}
      title={popUp.content.title}
      className={style.modal}
      closeModal={handleClose}
      showScrollbar
      danger={popUp.danger}
    >
      <div
        className={style.container}
        style={{
          '--bg-color': popUp.background.color,
          '--bg-image': popUp.background.image_url,
          '--bg-size': popUp.background.image_fit,
          '--bg-position': popUp.background.image_position,
          '--bg-opacity': popUp.background.opacity,
          '--bg-border-radius': popUp.background.border_radius
        }}
      >
        {popUp.content.subtitle && <h2>{popUp.content.subtitle}</h2>}
        {popUp.content.body && (
          <div
            className={style.content}
            dangerouslySetInnerHTML={sanitizeDirtyHTML(popUp.content.body)}
          />
        )}
        {popUp.content.image_url && (
          <img
            style={{
              margin: '0 auto',
              height: '400px'
            }}
            src={popUp.content.image_url}
            alt='Portal Pop Up'
          />
        )}
        {!isReadOnly && popUp.dont_show_again_checkbox && (
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={e => setDontShowAgain(e.target.checked)}
              />
            }
            label="Don't show this message again"
            className={style.checkbox}
          />
        )}

        {/* only show container if at least one button is shown */}
        {(popUp.show_dismiss_button ||
          popUp.show_primary_button ||
          popUp.show_secondary_button) && (
          <div className={style.buttonContainer}>
            {popUp.show_dismiss_button && (
              <Button
                onClick={handleClose}
                text='Dismiss'
                variant='default'
                fill
              />
            )}
            {popUp.show_secondary_button &&
              renderButton(
                popUp.secondary_button.label,
                popUp.secondary_button.variant,
                getButtonHandler(popUp.secondary_button, handleClose, navigate)
              )}
            {popUp.show_primary_button &&
              renderButton(
                popUp.primary_button.label,
                popUp.primary_button.variant,
                getButtonHandler(popUp.primary_button, handleClose, navigate)
              )}
          </div>
        )}
      </div>
    </Modal>
  );
}

PopUpCustomModal.propTypes = {
  popUp: propTypes.object.isRequired,
  closePopUp: propTypes.func.isRequired
};

function getButtonHandler(button, handleClose, navigate) {
  if (button.is_external) {
    return () => window.open(button.url, '_blank');
  }
  if (button.reload_page && !button.url && !button.path) {
    return () => {
      handleClose();
      window.location.reload();
    };
  }
  return () => {
    navigate(button.path);
    handleClose();
  };
}

function renderButton(label, variant, handleClick = () => {}) {
  return <Button fill onClick={handleClick} text={label} variant={variant} />;
}
